import { roleLabel } from '../../lib/roles'
import { useCallback, useEffect, useState } from 'react'
import { DataTable, ErrorBox, Field, Panel, ScreenHeading, Tag } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useT } from '../../lib/i18n'

interface UserRow {
  id: string
  email: string
  displayName: string
  role: 'rep' | 'supervisor' | 'admin'
  salesTeam: string | null
  active: boolean
  hasSso: boolean
  hasPassword: boolean
}

export function Users() {
  const { t } = useT()
  const [rows, setRows] = useState<UserRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', displayName: '', role: 'rep' as UserRow['role'], salesTeam: '', password: '' })
  const [busy, setBusy] = useState(false)
  const reload = useCallback(() => api.get<UserRow[]>('/api/admin/users').then(setRows).catch((e) => setError(errorMessage(e))), [])
  useEffect(() => {
    reload()
  }, [reload])

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/admin/users', {
        email: form.email,
        displayName: form.displayName,
        role: form.role,
        salesTeam: form.role === 'admin' ? null : form.salesTeam,
        password: form.password || undefined,
      })
      setForm({ email: '', displayName: '', role: 'rep', salesTeam: '', password: '' })
      await reload()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <ScreenHeading title={t('Users')} />
      {error && <div className="mb-4"><ErrorBox>{error}</ErrorBox></div>}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title={t('Provision a user')} subtitle={t('SSO users sign in with their Microsoft account once provisioned by email. A password is only needed without SSO.')} className="lg:col-span-1">
          <div className="space-y-3">
            <Field label={t('Email')}><input className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label={t('Display name')}><input className="field" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></Field>
            <Field label={t('Role')}>
              <select className="field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as UserRow['role'] })}>
                <option value="rep">{roleLabel('rep')}</option>
                <option value="supervisor">{roleLabel('supervisor')}</option>
                <option value="admin">{roleLabel('admin')}</option>
              </select>
            </Field>
            {form.role !== 'admin' && (
              <Field label={t('Sales team')}><input className="field" value={form.salesTeam} onChange={(e) => setForm({ ...form, salesTeam: e.target.value })} placeholder="AT-North" /></Field>
            )}
            <Field label={t('Password (optional)')}><input className="field" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <button type="button" className="btn btn-primary w-full" disabled={busy || !form.email || !form.displayName || (form.role !== 'admin' && !form.salesTeam)} onClick={create}>
              {t('Create')}
            </button>
          </div>
        </Panel>
        <Panel className="lg:col-span-2">
          <DataTable
            rows={rows ?? []}
            rowKey={(r) => r.id}
            columns={[
              { key: 'n', header: t('Name'), render: (r) => <span className="text-slate-200">{r.displayName}</span> },
              { key: 'e', header: t('Email'), render: (r) => <span className="text-slate-400">{r.email}</span> },
              { key: 'r', header: t('Role'), render: (r) => <Tag tone={r.role === 'admin' ? 'warn' : r.role === 'supervisor' ? 'info' : 'neutral'}>{roleLabel(r.role)}</Tag> },
              { key: 't', header: t('Team'), render: (r) => r.salesTeam ?? <span className="text-slate-600">{t('all')}</span> },
              { key: 'a', header: t('Sign-in'), render: (r) => <span className="text-xs text-slate-400">{[r.hasSso && 'SSO', r.hasPassword && t('password')].filter(Boolean).join(' · ') || t('not yet')}</span> },
              { key: 's', header: '', render: (r) => (r.active ? null : <Tag tone="neg">{t('inactive')}</Tag>) },
            ]}
          />
        </Panel>
      </div>
    </div>
  )
}
